import React, { useState, useEffect } from 'react';
import './App.css';
import Navbar from './components/layout/Navbar/Navbar';
import Footer from './components/layout/Footer/Footer';
import Hero from './components/sections/Hero/Hero';
import About from './components/sections/About/About';
import Services from './components/sections/Services/Services';
import Projects from './components/sections/Projects/Projects';
import Contact from './components/sections/Contact/Contact';
import { servicesApi, Service } from './api/servicesApi';
import { projectsApi, Project } from './api/projectsApi';
import AdminPortal from './components/admin/AdminPortal';

// API URL based on environment variable or default to localhost
const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

interface ApiStatus {
  status: 'online' | 'offline' | 'checking';
  message: string;
}

function App() {
  const isInternal = window.location.pathname === '/workshop-gate';

  const [apiStatus, setApiStatus] = useState<ApiStatus>({
    status: 'checking',
    message: 'Checking...'
  });

  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  const [isFallback, setIsFallback] = useState(false);

  // Check API status and load data on component mount
  useEffect(() => {
    const init = async () => {
      const isOnline = await checkApiStatus();
      setIsFallback(!isOnline);
      await loadServices();
      await loadProjects();
    };
    init();
  }, []);

  const loadServices = async () => {
    try {
      setServicesLoading(true);
      const serviceData = await servicesApi.getAllServices();
      setServices(serviceData);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setServicesLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      setProjectsLoading(true);
      const projectData = await projectsApi.getFeaturedProjects();
      setProjects(projectData);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const checkApiStatus = async (): Promise<boolean> => {
    try {
      setApiStatus({ status: 'checking', message: 'Checking...' });

      const response = await fetch(`${API_URL}/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setApiStatus({
          status: 'online',
          message: 'Backend Connected'
        });
        return true;
      } else {
        setApiStatus({
          status: 'offline',
          message: 'Backend Offline'
        });
        return false;
      }
    } catch (error) {
      setApiStatus({
        status: 'offline',
        message: 'Backend Offline'
      });
      return false;
    }
  };

  if (isInternal) {
    return <AdminPortal />;
  }

  return (
    <div className="App">
      <Navbar apiStatus={apiStatus} isFallback={isFallback} />
      {isFallback && (
        <div className="fallback-banner">
          ⚠️ Warning: Backend is offline. Displaying local demo data.
        </div>
      )}
      <main>
        <Hero />
        <About />
        <Services services={services} loading={servicesLoading} />
        <Projects projects={projects} loading={projectsLoading} />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;